import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { User } from '../types';
import { Language } from '../types/language';
import { adminListLanguages, createLanguage, updateLanguage, deleteLanguage } from '../api';
import Breadcrumb from '../components/Breadcrumb';
import Modal from '../components/Modal';
import ConfirmDialog from '../components/ConfirmDialog';
import { useToast } from '../components/Toast';
import { hasPermission } from '../utils/permissions';

interface LanguageFormState {
  code: string;
  name: string;
  enabled: boolean;
  displayOrder: number;
  isDefault: boolean;
}

const emptyForm: LanguageFormState = {
  code: '',
  name: '',
  enabled: true,
  displayOrder: 99,
  isDefault: false,
};

const AdminLanguageList: React.FC = () => {
  const { t } = useTranslation('admin');
  const toast = useToast();
  const currentUser: User | null = JSON.parse(localStorage.getItem('user') || 'null');
  const isAdmin = hasPermission(currentUser, 'platform_config');

  const [languages, setLanguages] = useState<Language[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingLanguage, setEditingLanguage] = useState<Language | null>(null);
  const [form, setForm] = useState<LanguageFormState>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // confirm delete
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Language | null>(null);

  useEffect(() => {
    if (!isAdmin) {
      setLoading(false);
      return;
    }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = async () => {
    try {
      setLoading(true);
      const data = await adminListLanguages();
      setLanguages(data || []);
    } catch (err: any) {
      toast.error(err.message || t('languages.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  if (!isAdmin) {
    return (
      <div className="container" style={{ maxWidth: '900px', margin: '0 auto', padding: '2rem 1rem' }}>
        <Breadcrumb items={[{ label: t('languages.breadcrumbHome'), to: '/projects' }, { label: t('languages.breadcrumbSkills'), to: '/language-skills' }, { label: t('languages.title') }]} />
        <p>{t('languages.needAdmin')}</p>
      </div>
    );
  }

  const openCreate = () => {
    setEditingLanguage(null);
    setForm(emptyForm);
    setError('');
    setShowModal(true);
  };

  const openEdit = (lang: Language) => {
    setEditingLanguage(lang);
    setForm({
      code: lang.code,
      name: lang.name,
      enabled: lang.enabled,
      displayOrder: lang.displayOrder,
      isDefault: lang.isDefault,
    });
    setError('');
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.code.trim()) { setError(t('languages.codeEmpty')); return; }
    if (!form.name.trim()) { setError(t('languages.nameEmpty')); return; }
    setSaving(true);
    setError('');
    try {
      if (editingLanguage) {
        const updated = await updateLanguage(editingLanguage.id, {
          code: form.code.trim(),
          name: form.name.trim(),
          enabled: form.enabled,
          displayOrder: form.displayOrder,
          isDefault: form.isDefault,
        });
        setLanguages(prev => prev.map(l =>
          l.id === editingLanguage.id
            ? updated
            : updated.isDefault ? { ...l, isDefault: false } : l
        ));
      } else {
        const created = await createLanguage({
          code: form.code.trim(),
          name: form.name.trim(),
          enabled: form.enabled,
          displayOrder: form.displayOrder,
          isDefault: form.isDefault,
        });
        setLanguages(prev => {
          const next = created.isDefault ? prev.map(l => ({ ...l, isDefault: false })) : prev;
          return [...next, created];
        });
      }
      setShowModal(false);
    } catch (err: any) {
      setError(err.message || t('languages.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (lang: Language) => {
    setDeleteTarget(lang);
    setConfirmDelete(true);
  };

  const doDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteLanguage(deleteTarget.id);
      setLanguages(prev => prev.filter(l => l.id !== deleteTarget.id));
    } catch (err: any) {
      toast.error(err.message || t('languages.deleteFailed'));
    } finally {
      setConfirmDelete(false);
      setDeleteTarget(null);
    }
  };

  const sorted = [...languages].sort((a, b) => {
    if (a.displayOrder !== b.displayOrder) return a.displayOrder - b.displayOrder;
    return a.code.localeCompare(b.code);
  });

  return (
    <div className="container" style={{ maxWidth: '900px', margin: '0 auto', padding: '2rem 1rem' }}>
      <Breadcrumb items={[{ label: t('languages.breadcrumbHome'), to: '/projects' }, { label: t('languages.title') }]} />
      <div className="page-header">
        <h1 className="page-title">{t('languages.title')}</h1>
        <button className="btn btn-primary" onClick={openCreate}>{t('languages.createLanguage')}</button>
      </div>

      {loading ? (
        <p>{t('loading', { ns: 'common' })}</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {sorted.length === 0 && <p style={{ color: 'var(--text-2)' }}>{t('languages.noLanguages')}</p>}
          {sorted.map(lang => (
            <div key={lang.id} className="list-item" style={{ alignItems: 'flex-start' }}>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem', flexWrap: 'wrap' }}>
                  <span style={{ background: 'var(--surface-2)', color: 'var(--text-2)', fontSize: '0.75rem', padding: '1px 8px', borderRadius: '10px', fontFamily: 'monospace' }}>{lang.code}</span>
                  <span style={{ fontWeight: 600, fontSize: '1rem' }}>{lang.name}</span>
                  <span style={{ fontSize: '0.75rem', color: 'var(--text-2)' }}>#{lang.displayOrder}</span>
                  {lang.enabled && <span style={{ background: '#dcfce7', color: '#15803d', fontSize: '0.7rem', padding: '1px 6px', borderRadius: '10px' }}>{t('languages.enabled')}</span>}
                  {lang.isDefault && <span style={{ background: 'var(--primary)', color: '#fff', fontSize: '0.7rem', padding: '1px 6px', borderRadius: '10px' }}>{t('languages.default')}</span>}
                </div>
              </div>
              <div className="list-item-actions" style={{ display: 'flex', gap: '0.5rem', flexShrink: 0, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                <button className="btn btn-ghost" style={{ fontSize: '0.8rem', padding: '0.3rem 0.75rem' }} onClick={() => openEdit(lang)}>{t('languages.edit')}</button>
                <button className="btn btn-danger" style={{ fontSize: '0.8rem', padding: '0.3rem 0.75rem' }} onClick={() => handleDelete(lang)}>{t('languages.delete')}</button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal
        open={showModal}
        onClose={() => setShowModal(false)}
        title={editingLanguage ? t('languages.editLanguage') : t('languages.createLanguageTitle')}
        width="500px"
        footer={
          <>
            <button className="btn btn-secondary" onClick={() => setShowModal(false)} disabled={saving}>{t('cancel', { ns: 'common' })}</button>
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? t('saving', { ns: 'common' }) : t('save', { ns: 'common' })}</button>
          </>
        }
      >
        <div className="form-group">
          <label className="form-label">{t('languages.codeLabel')}</label>
          <input
            className="input"
            value={form.code}
            onChange={e => setForm({ ...form, code: e.target.value })}
            placeholder={t('languages.codePlaceholder')}
          />
          {editingLanguage && (
            <p style={{ margin: '0.25rem 0 0', fontSize: '0.75rem', color: '#b45309' }}>
              {t('languages.codeChangeWarning')}
            </p>
          )}
        </div>
        <div className="form-group">
          <label className="form-label">{t('languages.nameLabel')}</label>
          <input
            className="input"
            value={form.name}
            onChange={e => setForm({ ...form, name: e.target.value })}
            placeholder={t('languages.namePlaceholder')}
          />
        </div>
        <div className="form-group">
          <label className="form-label">{t('languages.displayOrder')}</label>
          <input
            className="input"
            type="number"
            min={0}
            value={form.displayOrder}
            onChange={e => {
              const n = Number(e.target.value);
              setForm({ ...form, displayOrder: Number.isFinite(n) ? n : 0 });
            }}
          />
          <p style={{ margin: '0.25rem 0 0', fontSize: '0.75rem', color: 'var(--text-2)' }}>
            {t('languages.displayOrderHint')}
          </p>
        </div>
        <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <input
            type="checkbox"
            id="languageEnabled"
            checked={form.enabled}
            onChange={e => setForm({ ...form, enabled: e.target.checked })}
          />
          <label htmlFor="languageEnabled" style={{ margin: 0, cursor: 'pointer' }}>{t('languages.enabled')}</label>
        </div>
        <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <input
            type="checkbox"
            id="languageIsDefault"
            checked={form.isDefault}
            onChange={e => setForm({ ...form, isDefault: e.target.checked })}
          />
          <label htmlFor="languageIsDefault" style={{ margin: 0, cursor: 'pointer' }}>{t('languages.setDefault')}</label>
        </div>
        {error && <p className="error-text">{error}</p>}
      </Modal>

      <ConfirmDialog
        open={confirmDelete}
        onCancel={() => { setConfirmDelete(false); setDeleteTarget(null); }}
        onConfirm={doDelete}
        title={t('languages.deleteLanguageTitle')}
        message={t('languages.deleteLanguageMessage', { name: deleteTarget?.name ?? '' })}
        confirmText={t('languages.confirmDelete')}
        confirmDanger
      />
    </div>
  );
};

export default AdminLanguageList;
